import { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { isMasterUser } from './authSession';

const CACHE_KEY = 'acompanha_feature_flags';
const CACHE_EVENT = 'acompanha:features-updated';

export function readCachedFeatures() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) {
            return {};
        }
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

export function writeCachedFeatures(features) {
    const next = features && typeof features === 'object' ? features : {};
    localStorage.setItem(CACHE_KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(CACHE_EVENT, { detail: next }));
}

export function clearCachedFeatures() {
    localStorage.removeItem(CACHE_KEY);
    window.dispatchEvent(new CustomEvent(CACHE_EVENT, { detail: {} }));
}

export function normalizeFeatureEntry(value) {
    if (value && typeof value === 'object' && ('access' in value || 'show_in_menu' in value)) {
        return {
            access: Boolean(value.access),
            show_in_menu: Boolean(value.show_in_menu),
        };
    }
    if (typeof value === 'boolean') {
        return { access: value, show_in_menu: value };
    }
    return { access: false, show_in_menu: false };
}

export function getFeatureEntry(features, featureKey) {
    return normalizeFeatureEntry(features?.[featureKey]);
}

export function hasFeatureAccess(featureKey, features = readCachedFeatures()) {
    if (isMasterUser()) {
        return true;
    }
    return getFeatureEntry(features, featureKey).access;
}

export function resolveMenuItemState(item, { isMaster = false, features = readCachedFeatures() } = {}) {
    const master = isMaster || isMasterUser();
    if (item.masterOnly && !master) {
        return { visible: false, premiumLocked: false };
    }
    if (!item.featureFlag) {
        return { visible: true, premiumLocked: false };
    }
    if (master) {
        return { visible: true, premiumLocked: false };
    }
    const flag = getFeatureEntry(features, item.featureFlag);
    if (!flag.show_in_menu) {
        return { visible: false, premiumLocked: false };
    }
    return {
        visible: true,
        premiumLocked: !flag.access,
    };
}

export function canShowSidebarMenuItem(item, options = {}) {
    return resolveMenuItemState(item, options).visible;
}

export async function fetchFeatureFlags() {
    const previous = readCachedFeatures();
    try {
        const response = await api.get('/features');
        const features = response?.data?.data;
        if (features && typeof features === 'object') {
            writeCachedFeatures(features);
            return features;
        }
    } catch {
        // Mantém cache anterior quando a API falhar (rede/gRPC).
    }
    return previous;
}

export function useFeatureFlag(featureKey) {
    const [features, setFeatures] = useState(readCachedFeatures);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const next = await fetchFeatureFlags();
            setFeatures(next);
            return next;
        } catch {
            setFeatures(readCachedFeatures());
            return readCachedFeatures();
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        refresh().then(() => {
            if (cancelled) {
                return;
            }
        });
        const onUpdated = (event) => {
            if (event?.detail && typeof event.detail === 'object') {
                setFeatures(event.detail);
            }
        };
        window.addEventListener(CACHE_EVENT, onUpdated);
        return () => {
            cancelled = true;
            window.removeEventListener(CACHE_EVENT, onUpdated);
        };
    }, [refresh]);

    return {
        enabled: hasFeatureAccess(featureKey, features),
        loading,
        features,
        refresh,
    };
}
